/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, Form, Field, FieldProps } from 'formik';
import type { AppState, AppDispatch } from '@nextgen-web-framework/core';
import { AmwayPasswordInput, ComponentType, UsernameField } from '@commerce-identity-webcomponents/ui';
import {
  useTranslation,
  ButtonSize,
  Button,
  Input,
  InputTypes,
  ButtonStyles,
  ButtonTypes,
  Checkbox,
  MLPVersion,
  Theme,
  FAIcon,
  Notification,
  AccountType,
  Drawer,
  ButtonWidth,
  useBreakpoint,
  Modal,
  TextAlign,
  Breakpoints,
} from '@nextgen-web-framework/all';
import type { CreateChallengeRequest } from '@commerce-profile-webcomponents/sdk';
import {
  InputFormType,
  updateRegistrationData,
  profileApi,
  PersonalIdRecordTypeEnum,
} from '@commerce-profile-webcomponents/sdk';
import { OperationType } from '@commerce-identity-webcomponents/sdk';
import { defaultApiErrorHandler, isEmail, parseString } from '../../utils/helper';
import { checkUserExists, getJWTToken } from '../../utils/registrationUtils';
import RegistrationErrorModal from '../RegistrationErrorModal/RegistrationErrorModal';
import RegistrationOTPWrapper from '../RegistrationOTPWrapper/RegistrationOTPWrapper';
import ApiErrorModal from '../ApiErrorModal/ApiErrorModal';
import { InitialRegistrationInputFormValidationSchema } from '../../utils/ValidationSchema';
import { TextConstants } from '../../utils/Constants';
import { inputErrorMessage, inputErrorStatus } from '../Common/InputErrorComponent/utils';
import { useGlobalErrorHandler } from '../../utils/globalErrorHook';
import { ApiErrorModalProps } from '../ApiErrorModal/types/propTypes';
import { IdentityOnInputChangeCallbackResponse } from '../Registration/types/propTypes';
import RegistrationSponsor from '../RegistrationSponsor/RegistrationSponsor';
import { DoNotKnowSponsorFlow } from '../RegistrationSponsor/types/propTypes';
import styles from './SinglePageRegistrationStep.module.scss';
import { SinglePageRegistrationStepProps, RegistrationUserCheckType, IntialRegUserName } from './types/propTypes';

const SinglePageRegistrationStep = ({ nextHandler }: SinglePageRegistrationStepProps) => {
  const [disableButton, setDisableButton] = useState(true);
  const [isUniqueProfile, setIsUniqueProfile] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const checkboxRef = useRef<HTMLDivElement>(null);
  const { Customer, BusinessOwner } = AccountType;
  const selectedAccountType: AccountType = useSelector((state: AppState) => state.commerceProfile.selectedAccountType);
  const registrationFlowData = useSelector((state: AppState) => state.commerceProfile.registrationFlowData);
  const {
    OtpData,
    initialRegistrationForm,
    isRedirectedFromModal,
    isErrorModalOpen,
    openOtpDrawer,
    selectSponsor,
    userData,
    isIDVerified,
  } = registrationFlowData;
  const { inputForm } = initialRegistrationForm;
  const { sponsor } = selectSponsor;
  const { registrationId, jwtToken, patchVersion } = userData;
  const isFieldDisabled = selectedAccountType === Customer ? false : !isIDVerified;
  const [isOtpDrawerOpen, setIsOtpDrawerOpen] = useState(false);
  const [privacyDrawerOpen, setPrivacyDrawerOpen] = useState(false);
  const [tncDrawerOpen, setTncDrawerOpen] = useState(false);
  const [challengeId, setChallengeId] = useState<string>('');
  const [username, setUsername] = useState('');
  const apiErrorHandler = useGlobalErrorHandler();
  const enableFields = selectedAccountType === BusinessOwner;
  const personalDetailsClass = `${styles['personal-details']} ${enableFields ? styles['abo-user'] : ''}`;
  const updateIdNumber = () => {
    if (isIDVerified) {
      dispatch(
        updateRegistrationData({
          ...registrationFlowData,
          isIDVerified: false,
        }),
      );
    }
  };
  const t = useTranslation();
  const viewPortType = useBreakpoint();
  const onIDVerifyClick = (error?: string) => {
    if (error) {
      return;
    }
    dispatch(
      updateRegistrationData({
        ...registrationFlowData,
        isIDVerified: true,
      }),
    );
  };
  const onEnterVerify = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onIDVerifyClick();
    }
  };
  const dispatch = useDispatch<AppDispatch>();
  const openDrawer = () => {
    setIsOtpDrawerOpen(true);
  };
  const onCloseDrawer = () => {
    setIsOtpDrawerOpen(false);
  };
  useEffect(() => {
    if (isRedirectedFromModal) {
      setIsOtpDrawerOpen(false);
    } else if (openOtpDrawer) {
      setIsOtpDrawerOpen(true);
    }
  }, []);
  //username component state
  const [error, setError] = useState<string>('');

  const usernameProps = {
    dropdownLabel: t('profile.registration.username.countryText'),
    error,
    setError,
    defaultLabel: t('profile.registration.username.smartLabel'),
    mobileLabel: t('profile.registration.username.mobileText'),
    emailLabel: t('profile.registration.username.emailText'),
    onInputChangeCallback: ({ amwayId }: IdentityOnInputChangeCallbackResponse) => {
      setUsername(amwayId as string);
    },
    checkUsernameConfig: OperationType.CHECK,
    disabled: isFieldDisabled,
  };
  const onBlurUsername = (value: string) => {
    checkDuplicateUser({ userName: value });
  };

  useEffect(() => {
    if (tncDrawerOpen || (privacyDrawerOpen && !isDrawerOpen)) {
      if (checkboxRef.current && checkboxRef.current.scrollIntoView) {
        checkboxRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [isDrawerOpen, privacyDrawerOpen, tncDrawerOpen]);

  //password component props
  const passwordProps = {
    label: 'Password',
    disabled: isFieldDisabled,
  };

  const buttonTitle = t(
    selectedAccountType === Customer
      ? 'profile.SinglePageRegistrationStep.customerButton'
      : selectedAccountType === BusinessOwner
      ? 'profile.SinglePageRegistrationStep.aboButton'
      : 'profile.SinglePageRegistrationStep.memberButton',
  );

  const checkDuplicateUser = ({ userName }: IntialRegUserName) => {
    checkUserExists(userName, setIsUniqueProfile, apiErrorHandler).then((res: RegistrationUserCheckType) => {
      if (res.isUnique) {
        setDisableButton(false);
      } else {
        dispatch(
          updateRegistrationData({
            isErrorModalOpen: true,
          }),
        );
      }
    });
  };
  const tNcCheckboxHeading = t('profile.SinglePageRegistrationStep.tnccheckboxHeading');
  const privacyCheckboxHeading = t('profile.SinglePageRegistrationStep.privacycheckboxHeading');

  const checkboxDrawer = () => {
    const isMobileView = [Breakpoints.XS, Breakpoints.SM].indexOf(viewPortType as Breakpoints) !== -1;

    const displayText = t('profile.SinglePageRegistrationStep.checkboxContent');

    const buttonText = t('profile.SinglePageRegistrationStep.ButtonText');
    const headlineText = tncDrawerOpen ? tNcCheckboxHeading : privacyDrawerOpen ? privacyCheckboxHeading : '';
    const onClose = () => {
      setIsDrawerOpen(false);
    };
    const displayContent = (
      <div className={styles['checkbox-modal']}>
        <p data-testid="changeEmail-username-test-id">{displayText}</p>
        <p data-testid="changeEmail-username-test-id">{displayText}</p>
        <p data-testid="changeEmail-username-test-id">{displayText}</p>
        <p data-testid="changeEmail-username-test-id">{displayText}</p>
      </div>
    );
    return (
      <div data-testid="registration-change-username-drawer">
        {isMobileView ? (
          <Modal
            headline={headlineText}
            primaryButtonLabel={buttonText}
            onPrimaryButtonClick={onClose}
            onClose={onClose}
            textAlign={TextAlign.LEFT}
          >
            <div>{displayText}</div>
          </Modal>
        ) : (
          <Drawer
            header={headlineText}
            closeIcon
            className="form-drawer"
            onClose={onClose}
            bottomGap={0}
            footer={
              <Button
                data-testId="done-button-id"
                title={buttonText}
                buttonStyle={ButtonStyles.SOLID}
                size={ButtonSize.MEDIUM}
                onClick={onClose}
                buttonWidth={ButtonWidth.FULL_WIDTH}
              />
            }
          >
            <div className={styles['form-drawer']}>{displayContent}</div>
          </Drawer>
        )}
      </div>
    );
  };
  const registerationHandler = async (values: InputFormType, apiErrorHandler = defaultApiErrorHandler) => {
    const inputType = isEmail(username) ? 'email' : 'phone';
    const password = parseString(values.createPassword);
    const token = await getJWTToken(username, password, apiErrorHandler);
    // call challenge api
    const payload: CreateChallengeRequest = {
      [inputType]: username,
    };
    if (registrationId) {
      const challengeRes = await apiErrorHandler(() =>
        profileApi.postRegistrationChallenge({
          payload,
          registrationId,
        }),
      );

      setChallengeId(challengeRes.challengeId ?? '');
    }
    if (isUniqueProfile) {
      delete values.createPassword;
      const updatedData = {
        ...registrationFlowData,
        initialRegistrationForm: {
          ...registrationFlowData.initialRegistrationForm,
          inputForm: {
            ...values,
            userName: username,
          },
        },
        OtpData: {
          ...OtpData,
          inputType,
        },
        userData: {
          ...userData,
          jwtToken: token,
        },
      };
      dispatch(updateRegistrationData(updatedData));
      openDrawer();
      setPrivacyDrawerOpen(false);
      setTncDrawerOpen(false);
    }
  };
  const verifyCTAClassName = (idNumber?: string, error?: string) =>
    `${!idNumber || error ? styles['verify-link-disabled'] : styles['verify-link']}`;
  const payload = {
    givenName: inputForm.firstName,
    familyName: inputForm.lastName,
    privacyPolicy: inputForm.privacyPolicy,
    tncCheckbox: inputForm.tncCheckbox,
    identityJWT: jwtToken,
    personalId: { recordNumber: inputForm.idNumber, recordType: PersonalIdRecordTypeEnum.Cid },
    userName: inputForm.userName,
    otpRequestId: challengeId,
    version: patchVersion,
    sponsor: sponsor.accountNumber,
  };
  const [modalErrorData, setModalErrorData] = useState<ApiErrorModalProps>();
  const errorHandler = (code: string) => {
    setModalErrorData({
      headline: `profile.ApiErrorModal.${code}.headline`,
      body: `profile.ApiErrorModal.${code}.body`,
    });
  };

  const otpNextHandler = async () => {
    const response = await Promise.resolve(nextHandler(payload));
    if (response?.responseBody) {
      const { code } = response.responseBody;
      errorHandler(code);
    }
  };
  const otpArgs = {
    username: username,
    challengeId,
    nextHandler: () => otpNextHandler(), //TODO add code according to the otpWrapperComponent.
    onCloseDrawer,
  };
  return (
    <>
      {isDrawerOpen && checkboxDrawer()}
      {isErrorModalOpen && <RegistrationErrorModal />}
      {(modalErrorData?.headline || modalErrorData?.body) && <ApiErrorModal {...modalErrorData} />}
      <div
        className={styles['initial-registration-input-form-container']}
        data-testid="initial-registration-input-form-container"
      >
        {/* Below heading will be replaced with CMS data */}
        <div className={styles['input-form-heading']} data-testid="input-form-heading">
          {selectedAccountType === Customer
            ? t('profile.SinglePageRegistrationStep.headingCustomer')
            : selectedAccountType === BusinessOwner
            ? t('profile.SinglePageRegistrationStep.headingABO')
            : t('profile.SinglePageRegistrationStep.headingMember')}
        </div>
        <div className={styles['input-form']} data-testid="input-form">
          <Formik
            initialValues={inputForm}
            onSubmit={(values) => registerationHandler(values, apiErrorHandler)}
            validationSchema={InitialRegistrationInputFormValidationSchema}
            validateOnBlur={true}
          >
            {({ values, handleChange, handleBlur, setFieldValue }) => {
              const setChecboxValue = (check?: string) => {
                setFieldValue(`${check}`, !values[`${check}` as keyof InputFormType]);
              };
              const isNextButtonEnableCustomer =
                values.tncCheckbox &&
                values.privacyPolicy &&
                !disableButton &&
                username.length >= 4 &&
                error.length === 0 &&
                Object.keys(sponsor).length !== 0;
              const onClickTncLink = () => {
                setIsDrawerOpen(true);
                setTncDrawerOpen(true);
                setPrivacyDrawerOpen(false);
              };
              const onClickPrivacyLink = () => {
                setIsDrawerOpen(true);
                setPrivacyDrawerOpen(true);
                setTncDrawerOpen(false);
              };
              const onIdNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                updateIdNumber();
                handleChange(e);
              };
              return (
                <>
                  <Form>
                    {selectedAccountType !== Customer && (
                      <div className={styles['input-id-number-field']}>
                        <Field type="text" name="idNumber">
                          {({ meta }: FieldProps) => (
                            <>
                              <Input
                                id="idNumber"
                                data-testid="idNumber"
                                type={InputTypes.TEXT}
                                name="idNumber"
                                value={values.idNumber}
                                label={t('profile.SinglePageRegistrationStep.IDNumber')}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onIdNumberChange(e)}
                                onBlur={handleBlur}
                                mlpVersion={MLPVersion.TWO}
                                onKeyDown={onEnterVerify}
                                status={inputErrorStatus(meta.touched, meta.error)}
                                message={inputErrorMessage(t, meta.touched, meta.error, {
                                  max: TextConstants.ID_NUMBER_MAX,
                                })}
                                className={verifyCTAClassName(values.idNumber, meta.error)}
                                ctaTextLink={{
                                  text: t('profile.SinglePageRegistrationStep.verify'),
                                  id: 'btn_verify',
                                  onClick: () => onIDVerifyClick(meta.error),
                                  disabled: !values.idNumber,
                                }}
                                labelInfo={t('profile.SinglePageRegistrationStep.IDNumberTooltip')}
                              />
                              {isIDVerified && !meta.error && (
                                <span
                                  role="presentation"
                                  aria-label="message"
                                  className={`${styles['float-input-message']}`}
                                >
                                  {
                                    <FAIcon
                                      iconName={'circle-check'}
                                      theme={Theme.GREEN}
                                      className={styles['input-icon']}
                                    />
                                  }
                                  {
                                    <span className={styles['float-message']}>
                                      {t('profile.SinglePageRegistrationStep.IdCheckSuccessfully')}
                                    </span>
                                  }
                                </span>
                              )}
                            </>
                          )}
                        </Field>
                      </div>
                    )}

                    <div className={personalDetailsClass}>
                      <div className={styles['input-fname-field']}>
                        <Field type="text" name="firstName">
                          {({ meta }: FieldProps) => (
                            <>
                              <Input
                                id="firstName"
                                data-testid="firstName"
                                type={InputTypes.TEXT}
                                name="firstName"
                                value={values.firstName}
                                label={t('profile.SinglePageRegistrationStep.firstName')}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                mlpVersion={MLPVersion.TWO}
                                disabled={isFieldDisabled}
                                status={inputErrorStatus(meta.touched, meta.error)}
                                message={inputErrorMessage(t, meta.touched, meta.error, {
                                  max: TextConstants.NAME_MAX,
                                })}
                              />
                            </>
                          )}
                        </Field>
                      </div>

                      <div className={styles['input-lname-field']}>
                        <Field type="text" name="lastName">
                          {({ meta }: FieldProps) => (
                            <>
                              <Input
                                id="lastName"
                                data-testid="lastName"
                                type={InputTypes.TEXT}
                                name="lastName"
                                label={t('profile.SinglePageRegistrationStep.lastName')}
                                onChange={handleChange}
                                value={values.lastName}
                                onBlur={handleBlur}
                                mlpVersion={MLPVersion.TWO}
                                disabled={isFieldDisabled}
                                status={inputErrorStatus(meta.touched, meta.error)}
                                message={inputErrorMessage(t, meta.touched, meta.error, {
                                  max: TextConstants.NAME_MAX,
                                })}
                              />
                            </>
                          )}
                        </Field>
                      </div>

                      <div className={styles['userName-input-field']}>
                        <UsernameField {...usernameProps} onBlur={onBlurUsername} />
                      </div>
                      <div className={styles['password-input-field']}>
                        <AmwayPasswordInput
                          name="createPassword"
                          mlpVersion={MLPVersion.ONE}
                          {...passwordProps}
                          component={ComponentType.WITHOUT_CONFIRM}
                        />
                      </div>
                    </div>
                    <div className={styles['sponsor-details']}>
                      <RegistrationSponsor
                        isFieldDisabled={isFieldDisabled}
                        showABOSelected={false}
                        isFocused={false} //TODO if we want sponsorFailed state and focus this element.
                        doNotKnowAboFlowType={DoNotKnowSponsorFlow.POSTAL_CODE_SEARCH}
                      />
                    </div>
                    {/* data will be added from CMS later on and cross icon will be removed */}
                    <div className={styles['warning-notification']}>
                      <Notification
                        message=""
                        description={t('profile.SinglePageRegistrationStep.warningNotification')}
                        theme={Theme.WARNING}
                        zIndex={2}
                      />

                      <div className={styles['tnc-checkbox-wrapper']} ref={checkboxRef}>
                        <div className={styles['tnc-checkbox']}>
                          <Checkbox
                            data-testid="tnc-checkbox-one"
                            text={''}
                            checked={values.privacyPolicy}
                            onChange={() => setChecboxValue('privacyPolicy')}
                          />
                          <div className={styles['tnc-text']}>
                            <span>
                              <span
                                className={`${styles['tnc-textlink']} ${
                                  values.privacyPolicy ? styles['tnc-textlink-checked'] : ''
                                }`}
                              >
                                {t('profile.SinglePageRegistrationStep.checkbox1')}
                              </span>

                              {/* link will be added in future */}
                              <a
                                className={styles['tnc-hyperlink']}
                                href="#"
                                onClick={onClickPrivacyLink}
                                data-testid="privacy-checkbox-link"
                              >
                                {t('profile.SinglePageRegistrationStep.privacy-checkbox')}
                              </a>
                            </span>
                          </div>
                        </div>
                        <div className={styles['tnc-checkbox']}>
                          <Checkbox
                            data-testid="tnc-checkbox-two"
                            text={''}
                            checked={values.tncCheckbox}
                            onChange={() => setChecboxValue('tncCheckbox')}
                          />
                          <div className={styles['tnc-text']}>
                            <span>
                              <span
                                className={`${styles['tnc-textlink']} ${
                                  values.tncCheckbox ? styles['tnc-textlink-checked'] : ''
                                }`}
                              >
                                {t('profile.SinglePageRegistrationStep.checkbox1')}
                              </span>

                              {/* link will be added in future */}
                              <a
                                className={styles['tnc-hyperlink']}
                                href="#"
                                onClick={onClickTncLink}
                                data-testid="tnc-checkbox-link"
                              >
                                {t('profile.SinglePageRegistrationStep.TNCcheckbox')}
                              </a>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={styles['register-btn']}>
                      <Button
                        title={buttonTitle}
                        type={ButtonTypes.SUBMIT}
                        data-testid="next-button-testid"
                        buttonStyle={ButtonStyles.SOLID}
                        size={ButtonSize.MEDIUM}
                        disabled={!isNextButtonEnableCustomer}
                      />
                    </div>
                    {isOtpDrawerOpen && <RegistrationOTPWrapper {...otpArgs} />}
                  </Form>
                </>
              );
            }}
          </Formik>
        </div>
      </div>
    </>
  );
};
export default SinglePageRegistrationStep;
